import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../lib/api";
import env from "../../env";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";
import type { PersonalizationField, OrderTextProof } from "@erc/shared";
import { PrintCheckPanel } from "../design/PrintCheckPanel";
import { X, Loader2, Sparkles, Download, Upload, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

const abs = (u?: string) => (!u ? "" : u.startsWith("http") ? u : `${env.API_URL}${u}`);

// Referans (analiz listesi / secili tasarim) + personalization degerleriyle
// SADIK sipariş tasarimi uretir. Yeni siparis geldiginde buradan uretilir.
export function OrderDesignModal({ analysisId, referenceImageUrl: sourceImageUrl, fields, productTypeKey, onClose }: {
  analysisId: string;
  /** Analizin urun tipi: baski denetiminde varsayilan sablon */
  productTypeKey?: string | null;
  referenceImageUrl?: string;
  fields: PersonalizationField[];
  onClose: () => void;
}) {
  const { models, model: effectiveModel, setModel } = useDesignModel("erc:model:order-design");
  const [values, setValues] = useState<Record<number, string>>({});
  const [images, setImages] = useState<Record<number, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ imageUrl?: string; sku?: string; textProof?: OrderTextProof | null } | null>(null);
  const [proofing, setProofing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Referans: kolaj/urun fotografi DEGIL, bizim TASARIMIMIZ. Varsayilan en son
  // custom tasarim; yoksa en son tasarim; hic yoksa ilham gorseli (yedek).
  const { data: designs = [] } = useQuery({
    queryKey: ["analysis-designs", analysisId],
    queryFn: () => apiClient.listAnalysisDesigns(analysisId),
  });
  const ready = (designs as Array<{ id: string; sku?: string | null; prompt?: string; status: string; imageUrl?: string | null }>)
    .filter((d) => d.status === "completed" && d.imageUrl);
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (referenceImageUrl || ready.length === 0) return;
    const custom = ready.find((d) => d.prompt?.startsWith("CUSTOM ("));
    setReferenceImageUrl((custom ?? ready[0]).imageUrl ?? undefined);
  }, [ready, referenceImageUrl]);
  const effectiveRef = referenceImageUrl ?? (ready.length === 0 ? sourceImageUrl : undefined);

  function onFile(i: number, files: FileList | null) {
    if (!files) return;
    const arr = Array.from(files).slice(0, 5);
    Promise.all(arr.map((f) => new Promise<string>((res) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result));
      r.readAsDataURL(f);
    }))).then((urls) => setImages((prev) => ({ ...prev, [i]: urls })));
  }

  async function generate() {
    setBusy(true); setError(null); setResult(null);
    try {
      const allImages: string[] = [];
      const payloadValues = buildValues(allImages);
      const r = await apiClient.generateOrderDesign(analysisId, {
        values: payloadValues, referenceImageUrl: effectiveRef, customerImages: allImages, model: effectiveModel,
      });
      setResult({ imageUrl: r.imageUrl, sku: r.sku, textProof: r.textProof });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Üretilemedi");
    } finally {
      setBusy(false);
    }
  }

  function buildValues(allImages: string[] = []) {
    return fields.map((f, i) => {
        if (f.type === "text_input" || f.type === "dropdown") return { label: f.label, type: f.type, value: values[i] || "" };
        const imgs = images[i] || [];
        allImages.push(...imgs);
        return { label: f.label, type: f.type, imageCount: imgs.length };
    });
  }

  async function reproof() {
    if (!result?.imageUrl) return;
    setProofing(true);
    try {
      const r = await apiClient.proofOrderDesign(analysisId, { imageUrl: result.imageUrl, values: buildValues() });
      setResult((p) => (p ? { ...p, textProof: r.textProof } : p));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Yazı kontrol edilemedi");
    } finally {
      setProofing(false);
    }
  }

  async function download() {
    if (!result?.imageUrl) return;
    setDownloading(true);
    try {
      const res = await fetch(abs(result.imageUrl));
      const blob = await res.blob();
      const a = document.createElement("a");
      const u = URL.createObjectURL(blob);
      a.href = u; a.download = `${result.sku || "siparis"}.png`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch {
      window.open(abs(result.imageUrl), "_blank");
    } finally { setDownloading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900"><Sparkles className="h-5 w-5 text-indigo-600" /> Sipariş Tasarımı Üret</h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 md:flex-row">
          {/* Sol: referans + form */}
          <div className="flex-1 space-y-3">
            {effectiveRef && (
              <div>
                <span className="text-xs text-gray-500">
                  Referans {ready.length === 0 ? "(tasarım yok — ilham görseli kullanılıyor)" : "tasarım"}
                </span>
                <img src={abs(effectiveRef)} alt="" className="mt-1 h-40 w-full rounded-lg border border-gray-200 object-contain bg-[repeating-conic-gradient(#e5e7eb_0_25%,#fff_0_50%)] bg-[length:16px_16px]" />
              </div>
            )}
            {ready.length > 1 && (
              <div>
                <span className="text-[11px] text-gray-500">Referans tasarımı seç:</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {ready.map((d) => (
                    <button key={d.id} onClick={() => setReferenceImageUrl(d.imageUrl ?? undefined)} title={d.sku ?? ""}
                      className={`h-14 w-14 overflow-hidden rounded border-2 bg-gray-50 ${effectiveRef === d.imageUrl ? "border-indigo-500" : "border-gray-200"}`}>
                      <img src={abs(d.imageUrl ?? "")} alt="" className="h-full w-full object-contain" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500">Sipariş bilgilerini gir; referans birebir yeniden üretilip bu değerler uygulanır.</p>

            {fields.map((f, i) => (
              <div key={i}>
                <label className="text-xs font-medium text-gray-600">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                {f.instructions && <p className="text-[10px] text-gray-400">{f.instructions}</p>}
                {f.type === "text_input" && (
                  <input value={values[i] || ""} onChange={(e) => setValues((p) => ({ ...p, [i]: e.target.value }))}
                    maxLength={f.maxChar || 256} placeholder="Sipariş değeri"
                    className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                )}
                {f.type === "dropdown" && (
                  <select value={values[i] || ""} onChange={(e) => setValues((p) => ({ ...p, [i]: e.target.value }))}
                    className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
                    <option value="">Seç…</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                )}
                {(f.type === "unlabeled_upload" || f.type === "labeled_upload") && (
                  <div className="mt-0.5">
                    <label className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs text-gray-500 hover:bg-gray-50">
                      <Upload className="h-3.5 w-3.5" /> {images[i]?.length ? `${images[i].length} görsel seçildi` : "Müşteri görseli yükle"}
                      <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFile(i, e.target.files)} />
                    </label>
                    {f.options && f.options.length > 0 && <p className="mt-0.5 text-[10px] text-gray-400">Slotlar: {f.options.join(", ")}</p>}
                  </div>
                )}
              </div>
            ))}

            <div>
              <label className="text-xs font-medium text-gray-600">AI Modeli</label>
              <select value={effectiveModel} onChange={(e) => setModel(e.target.value)}
                className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm">
                {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
              </select>
            </div>

            {error && <p className="text-xs text-red-600">{error}</p>}
            <button onClick={generate} disabled={busy || !effectiveModel}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "Üretiliyor… (~30sn)" : "Sipariş Tasarımını Üret"}
            </button>
          </div>

          {/* Sag: sonuc */}
          <div className="flex w-full flex-col md:w-72">
            <span className="text-xs text-gray-500">Sonuç</span>
            <div className="mt-1 flex flex-1 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2" style={{ minHeight: 220 }}>
              {result?.imageUrl ? (
                <img src={abs(result.imageUrl)} alt="" className="max-h-72 w-full object-contain" />
              ) : (
                <span className="text-xs text-gray-400">{busy ? "Üretiliyor…" : "Henüz üretilmedi"}</span>
              )}
            </div>
            {result?.imageUrl && (
              <>
                <p className="mt-1 text-[11px] text-emerald-600">✓ Tasarım havuzuna kaydedildi{result.sku ? ` (${result.sku})` : ""}</p>
                <TextProofPanel proof={result.textProof} hasText={fields.some((f, i) => f.type === "text_input" && values[i]?.trim())}
                  proofing={proofing} onReproof={reproof} />
                <PrintCheckPanel imageUrl={result.imageUrl} productTypeKey={productTypeKey} />
                <button onClick={download} disabled={downloading}
                  className="mt-2 flex items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50">
                  {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} İndir
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Musterinin yazdigi metin tasarimda harf harf var mi — AI "duzeltebilir"
function TextProofPanel({ proof, hasText, proofing, onReproof }: {
  proof?: OrderTextProof | null; hasText: boolean; proofing: boolean; onReproof: () => void;
}) {
  if (!hasText) return null;
  const again = (
    <button onClick={onReproof} disabled={proofing} className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50">
      {proofing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Tekrar kontrol et
    </button>
  );
  if (!proof) {
    return (
      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
        Yazı otomatik kontrol edilemedi — göndermeden önce harf harf kendin kontrol et. <div className="mt-1">{again}</div>
      </div>
    );
  }
  const bad = proof.status === "mismatch";
  return (
    <div className={`mt-2 rounded-lg border p-2 text-xs ${bad ? "border-red-200 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
      <p className="flex items-center gap-1 font-medium">
        {bad ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
        {bad ? "Müşteri yazısı tasarımda birebir yok" : "Müşteri yazısı tasarımda birebir var"}
      </p>
      {proof.fields.map((f) => (
        <div key={f.label} className="mt-1">
          <span className="text-gray-500">{f.label}: </span>
          {f.words.map((w, i) => (
            <span key={i} className={`mr-1 rounded-lg px-1 ${w.status === "missing" ? "bg-red-200 font-semibold" : w.status === "case" ? "bg-amber-100" : ""}`}
              title={w.status === "case" ? "Var ama büyük/küçük harf ya da aralık farklı" : undefined}>
              {w.word}{w.status === "missing" && w.closest ? ` → ${w.closest}` : ""}
            </span>
          ))}
        </div>
      ))}
      <p className="mt-1 text-gray-500">Tasarımda okunan: {proof.found.length ? proof.found.map((t) => `"${t}"`).join(", ") : "(yazı yok)"}</p>
      {bad && <p className="mt-1">Yeniden üret ya da düzelt; müşteriye bu haliyle gönderme.</p>}
      <div className="mt-1">{again}</div>
    </div>
  );
}
